"use client";

import { useEffect, useState } from "react";
import useTitle from "../../shared/hooks/useTitle";
import Modal from "../../shared/components/Modal";
import MODAL_NAMES from "../dialogs/ModalName";
import { observer } from "mobx-react-lite";
import { useAppContext } from "../../shared/functions/Context";
import { LoadingEllipsis } from "../../shared/components/loading/Loading";
import { useNavigate, useParams } from "react-router-dom";
import useBackButton from "../../shared/hooks/useBack";
import useIndividualScorecard from "../../shared/hooks/useIndividualScorecard";
import EmployeeQ2ReviewCycle from "./EmployeeQ2ReviewCycle";

import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary";
import SubmitScorecardDisputeModal from "../dialogs/submit-dispute/SubmitMidtermDisputeModal";

import { ArrowLeft, AlertCircle, FileText, CheckCircle } from "lucide-react";

import EmployeeQ4ReviewCycle from "./EmployeeQ4ReviewCycle";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../dashboard/company-dashboard/ui/tabs";
import SubmitFinalAssessmentDisputeModal from "../dialogs/submit-dispute/SubmitFinalAssessmentDisputeModal";

const DisputeEmployeeScorecardView = observer(() => {
  const { store, api, ui } = useAppContext();
  const { uid } = useParams();

  const [loading, setLoading] = useState(false);
  const [_, setTitle] = useTitle("Submit Disputes");
  const [activeTab, setActiveTab] = useState("midterm");

  const objectives = store.objective.getByUid(uid!);
  const measures = store.measure.getByUid(uid!);

  const selectedUser = store.user.selected;
  const currentUser = store.auth.meJson;
  const agreement = useIndividualScorecard(uid);

  useBackButton("/c/scorecards/my");
  const navigate = useNavigate();

  const midtermDisputedMeasures = measures.filter(
    (m) => m.asJson.isDispute===true
  );
  const finalDisputedMeasures = measures.filter(
    (m) => m.asJson.isDispute===true
  );
  const totalMeasures = measures.length;

  const currentDisputedMeasures =
    activeTab === "midterm" ? midtermDisputedMeasures : finalDisputedMeasures;
  const disputePercentage =
    totalMeasures > 0
      ? Math.round((currentDisputedMeasures.length / totalMeasures) * 100)
      : 0;

  const handleBack = () => {
    navigate("/c/scorecards/my");
  };

  useEffect(() => {
    const load = async () => {
      if (!uid) {
        ui.snackbar.load({
          id: Date.now(),
          type: "danger",
          message: "Invalid user ID",
          timeoutInMs: 5000,
        });
        navigate("/c/scorecards/my");
        return;
      }
      setLoading(true);
      try {
        await api.objective.getAll(uid);
        await api.measure.getAll(uid);
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          type: "danger",
          message: "Failed to load scorecard data",
          timeoutInMs: 5000,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api.measure, api.objective, uid, navigate, ui.snackbar]);

  return (
    <ErrorBoundary>
      <div className="bg-gradient-to-r from-[#84cbe9] to-[#3f6a8e] text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          {/* Back Button */}
          {/* <button
            onClick={handleBack}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-4 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Scorecards</span>
          </button> */}

          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">Dispute Dashboard</h1>
              <p className="text-white/90">
                {selectedUser?.displayName ||
                  currentUser?.displayName ||
                  "Employee"}{" "}
                - Scorecard Disputes
              </p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
              <p className="text-sm text-white/80">Status</p>
              <p className="text-lg font-semibold">
                {agreement.isDispute ? "Dispute Submitted" : "No Disputes"}
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-3">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-white/80">Total Measures</p>
                  <p className="text-2xl font-bold">{totalMeasures}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="flex items-center gap-3">
                <div className="bg-[#faa05a]/20 rounded-full p-3">
                  <AlertCircle className="w-6 h-6 text-[#faa05a]" />
                </div>
                <div>
                  <p className="text-sm text-white/80">Disputed Measures</p>
                  <p className="text-2xl font-bold">
                    {currentDisputedMeasures.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="flex items-center gap-3">
                <div className="bg-[#4bb543]/20 rounded-full p-3">
                  <CheckCircle className="w-6 h-6 text-[#4bb543]" />
                </div>
                <div>
                  <p className="text-sm text-white/80">Dispute Rate</p>
                  <p className="text-2xl font-bold">{disputePercentage}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!loading && currentDisputedMeasures.length === 0 && (
        <div className="w-full px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-blue-50 border-l-4 border-[#84cbe9] p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#84cbe9] mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-[#3f6a8e] mb-1">
                  No Disputes Submitted
                </h3>
                <p className="text-sm text-gray-700">
                  Review your scorecard measures below. If you disagree with any
                  ratings, click on the measure to submit a dispute.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content with Tabs */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {!loading && (
          <ErrorBoundary>
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
                <TabsTrigger value="midterm" className="text-sm font-medium">
                  Midterm Dispute (Q2)
                </TabsTrigger>
                <TabsTrigger value="final" className="text-sm font-medium">
                  Final Assessment Dispute (Q4)
                </TabsTrigger>
              </TabsList>

              <TabsContent value="midterm" className="mt-0">
                <EmployeeQ2ReviewCycle
                  agreement={agreement}
                  selectedUser={selectedUser!}
                  currentUser={currentUser!}
                  measures={measures}
                  objectives={objectives}
                />
              </TabsContent>

              <TabsContent value="final" className="mt-0">
                <EmployeeQ4ReviewCycle
                  agreement={agreement}
                  selectedUser={selectedUser!}
                  currentUser={currentUser!}
                  measures={measures}
                  objectives={objectives}
                />
              </TabsContent>
            </Tabs>
          </ErrorBoundary>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <LoadingEllipsis />
          </div>
        )}
      </div>

      <Modal modalId={MODAL_NAMES.EXECUTION.SCORECARD_DISPUTE_Q2_MODAL}>
        <SubmitScorecardDisputeModal />
      </Modal>

      <Modal modalId={MODAL_NAMES.EXECUTION.SCORECARD_DISPUTE_Q4_MODAL}>
        <SubmitFinalAssessmentDisputeModal />
      </Modal>
    </ErrorBoundary>
  );
});

export default DisputeEmployeeScorecardView;
